/**
 * Centrale styling definities voor Spelveilingen
 * 
 * Dit bestand bevat herbruikbare style objecten om consistentie
 * te garanderen en duplicatie te voorkomen.
 * 
 * Gebruik: import { styles } from '../theme/commonStyles';
 *         <Box sx={styles.pageHeader}>
 */

// =============================================================================
// LAYOUT STYLES
// =============================================================================

export const layout = {
    /** Flexbox row met items gecentreerd */
    flexCenter: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },

    /** Flexbox row met space-between */
    flexBetween: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },

    /** Flexbox row met items links uitgelijnd */
    flexStart: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },

    /** Flexbox kolom */
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },

    /** Volledige hoogte centered (voor loading states) */
    fullHeightCenter: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
    },
};

// =============================================================================
// CONTAINER STYLES
// =============================================================================

export const containers = {
    /** Glassmorphism container voor tabellen en kaarten */
    glassContainer: {
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2, // 8px in MUI spacing
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        overflow: 'hidden',
    },

    /** Paper met standaard padding */
    paperSection: {
        p: 3,
        mb: 3,
    },

    /** Paper voor modals/dialogs */
    paperModal: {
        p: 2,
    },

    /** Mobile card styling */
    mobileCard: {
        p: 1.5,
        borderRadius: 2,
    },

    /** Mobile card list container */
    mobileCardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
    },
};

// =============================================================================
// PAGE STRUCTURE STYLES
// =============================================================================

export const page = {
    /** Standaard page header met titel en acties */
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
    },

    /** Page header alleen met titel (geen acties) */
    headerSimple: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
    },

    /** Section header binnen een pagina */
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
    },

    /** Loading state container */
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 4,
    },

    /** Loading state full screen centered */
    fullscreenLoadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
    },

    /** Loading state full page */
    loadingFullPage: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
    },
};

// =============================================================================
// SPACING PRESETS
// =============================================================================

export const spacing = {
    /** Standaard gap tussen items */
    gap: {
        xs: 1,
        sm: 1.5,
        md: 2,
        lg: 3,
    },

    /** Standaard margin bottom */
    mb: {
        xs: 1,
        sm: 2,
        md: 3,
        lg: 4,
    },

    /** Standaard margin top */
    mt: {
        xs: 1,
        sm: 2,
        md: 3,
        lg: 4,
    },
};

// =============================================================================
// AVATAR SIZES
// =============================================================================

export const avatarSizes = {
    /** Extra klein - voor inline icons */
    xs: { width: 20, height: 20 },

    /** Klein - voor club logos in tabellen */
    sm: { width: 24, height: 24 },

    /** Medium - voor speler avatars in tabellen */
    md: { width: 32, height: 32 },

    /** Large - voor mobile cards */
    lg: { width: 48, height: 48 },

    /** Extra large - voor modals/details */
    xl: { width: 60, height: 60 },

    /** XXL - voor profiel pagina's */
    xxl: { width: 80, height: 80 },
};

// =============================================================================
// TABLE STYLES
// =============================================================================

export const table = {
    /** Standaard tabel cell met pointer cursor */
    clickableCell: {
        cursor: 'pointer',
    },

    /** Tabel cell met drag handle */
    dragCell: {
        cursor: 'grab',
        width: '40px',
    },

    /** Narrow column (voor rankings, nummers) */
    narrowColumn: {
        width: '60px',
    },

    /** Action column */
    actionColumn: {
        width: '80px',
    },

    /** Highlighted row (voor huidige gebruiker) */
    highlightedRow: {
        backgroundColor: 'action.hover',
        '& > .MuiTableCell-root': {
            fontWeight: 'bold',
        },
    },

    /** Bold primary color cell (voor punten) */
    primaryBoldCell: {
        fontWeight: 'bold',
        color: 'primary.main',
    },
};

// =============================================================================
// FORM STYLES
// =============================================================================

export const form = {
    /** Form container met standaard spacing */
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },

    /** Centered form (voor login/register) */
    centeredForm: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 400,
        mx: 'auto',
        p: 3,
    },

    /** Form control met standaard margin */
    formControl: {
        mb: 2,
        minWidth: 200,
    },

    /** Inline form row */
    inlineRow: {
        display: 'flex',
        gap: 2,
        alignItems: 'center',
    },
};

// =============================================================================
// BUTTON STYLES
// =============================================================================

export const button = {
    /** Button met margin right (in button groups) */
    withMarginRight: {
        mr: 1,
    },

    /** Icon button met geen padding */
    iconOnly: {
        p: 0,
    },
};

// =============================================================================
// TEXT STYLES
// =============================================================================

export const text = {
    /** Secondary text (voor subtitels, captions) */
    secondary: {
        color: 'text.secondary',
    },

    /** Small caption text */
    caption: {
        color: 'text.secondary',
        fontSize: '0.75rem',
    },

    /** Extra small caption */
    captionSmall: {
        color: 'text.secondary',
        fontSize: '0.7rem',
    },

    /** Bold text */
    bold: {
        fontWeight: 'bold',
    },

    /** Clickable text (underlined) */
    clickable: {
        cursor: 'pointer',
        textDecoration: 'underline',
    },

    /** No wrap text */
    noWrap: {
        whiteSpace: 'nowrap',
    },
};

// =============================================================================
// MODAL/DIALOG STYLES
// =============================================================================

export const modal = {
    /** Content section met margin */
    contentSection: {
        mb: 2,
    },

    /** Player info row in modal */
    playerInfoRow: {
        display: 'flex',
        alignItems: 'center',
        mb: 2,
    },

    /** Avatar met margin right */
    avatarWithMargin: {
        mr: 2,
    },
};

// =============================================================================
// POSITION CHIP HELPERS
// =============================================================================

/**
 * Geeft de positie-specifieke styling terug voor een chip
 * @param {object} theme - MUI theme object
 * @param {string} position - Positie naam (Keeper, Verdediger, etc.)
 * @returns {object} Style object voor de chip
 */
export const getPositionChipStyle = (theme, position) => {
    const positionStyle = theme.positions?.[position];
    if (positionStyle) {
        return {
            backgroundColor: positionStyle.backgroundColor,
            color: positionStyle.color,
            fontWeight: 'bold',
        };
    }
    return {};
};

// =============================================================================
// RESPONSIVE HELPERS
// =============================================================================

export const responsive = {
    /** Verberg op mobile */
    hideOnMobile: {
        display: { xs: 'none', md: 'flex' },
    },

    /** Toon alleen op mobile */
    showOnMobile: {
        display: { xs: 'flex', md: 'none' },
    },

    /** Verberg op mobile (block variant) */
    hideOnMobileBlock: {
        display: { xs: 'none', md: 'block' },
    },

    /** Toon alleen op mobile (block variant) */
    showOnMobileBlock: {
        display: { xs: 'block', md: 'none' },
    },

    /** Responsive flex direction */
    responsiveDirection: {
        flexDirection: { xs: 'column', md: 'row' },
    },

    /** Responsive gap */
    responsiveGap: {
        gap: { xs: 2, md: 4 },
    },
};

// =============================================================================
// COMBINED PRESETS (voor veel voorkomende combinaties)
// =============================================================================

export const presets = {
    /** Tabel in glass container */
    tableInGlassContainer: {
        ...containers.glassContainer,
    },

    /** Page met loading state */
    pageLoading: {
        ...page.loadingContainer,
    },

    /** Mobile card met content layout */
    mobileCardWithContent: {
        ...containers.mobileCard,
        ...layout.flexColumn,
    },

    /** Header met flexbox between */
    headerWithActions: {
        ...layout.flexBetween,
        mb: 3,
    },

    /** Form in centered container */
    centeredFormContainer: {
        ...form.centeredForm,
    },
};

// =============================================================================
// DEFAULT EXPORT - Alle styles gebundeld
// =============================================================================

const styles = {
    layout,
    containers,
    page,
    spacing,
    avatarSizes,
    table,
    form,
    button,
    text,
    modal,
    responsive,
    presets,
    getPositionChipStyle,
};

export default styles;
